<template>
  <div v-if="selectedPlan !== null && selectedPlan !== undefined">
    <div v-if="editedItem !== null && editedItem !== undefined">
      <pensions-plan-identity
        :edit="edit"
        @saved="saved"
        :originalItem="editedItem"
        v-if="editedItem.PlanType === 'Pension'"
      ></pensions-plan-identity>
      <plan-identity
        :edit="edit"
        @saved="saved"
        :originalItem="editedItem"
        v-else
      ></plan-identity>
      <template
        v-if="editedItem.PlanType === 'HW' || editedItem.PlanType === 'Pension'"
      >
        <v-dialog
          scrollable
          v-model="dialogs.flag"
          persistent
          max-width="500px"
        >
          <material-card
            :title="$t(flagFormTitle)"
            :icon="flagFormIcon"
            :color="formColor"
            v-if="flag !== null && flag !== undefined"
          >
            <v-card-text>
              <flag-form
                :item="flag"
                :disabled="false"
                ref="flagForm"
                :dialog="dialogs.flag"
                @valid-changed="flagValid = $event"
              ></flag-form>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="closeFlag">{{ $t("globals.cancel") }}</v-btn>
              <v-btn
                :disabled="!flagValid"
                color="primary"
                :loading="saving"
                @click="saveFlag"
              >
                {{ $t("globals.save") }}
              </v-btn>
            </v-card-actions>
          </material-card>
        </v-dialog>
        <v-dialog
          v-model="dialogs.delete"
          scrollable
          persistent
          max-width="400"
        >
          <material-card
            :title="$t('views.plangeneralparameters.remove_flag')"
            :color="$vuetify.theme.themes.light.error"
            icon="delete"
          >
            <v-card-text>
              {{ $t("globals.are_you_sure") }}
            </v-card-text>
            <v-card-actions>
              <v-btn :loading="saving" text @click="dialogs.delete = false">
                {{ $t("globals.cancel") }}
              </v-btn>
              <v-btn :loading="saving" color="error" @click="removeFlag">
                {{ $t("globals.remove") }}
              </v-btn>
            </v-card-actions>
          </material-card>
        </v-dialog>
        <material-card
          :title="$t('views.plangeneralparameters.flags')"
          :color="$vuetify.theme.themes.light.secondary"
          icon="flag"
        >
          <pbas-data-table
            ref="table"
            :exporters="$exporters"
            :dataProvider="flagsProvider"
            :rowActions="rowActions"
            :floatActions="floatActions"
            fileNamePrefix="flags"
            identifier="flagsTable"
            @error="error = $event"
          ></pbas-data-table>
        </material-card>
        <contacts
          identifier="inHouseContacts"
          entity="InHouse"
          filter="IsInHouse eq true"
          v-if="editedItem.PlanType === 'HW'"
        ></contacts>
        <material-card
          :title="
            $t('components.vuematerialdashboard.core.appbar.pension_regulators')
          "
          :color="$vuetify.theme.themes.light.secondary"
          icon="mdi-scale-balance"
          v-if="editedItem.PlanType === 'Pension'"
        >
          <pbas-data-table
            :exporters="$exportersWithHistory"
            ref="table"
            :dataProvider="regulatorsProvider"
            fileNamePrefix="pension-regulators"
            identifier="pensionRegulatorsTable"
            :canExpand="(item) => item.HasHistory"
            :showExpand="true"
            :generateProviderForExpanded="(item) => generateProvider(item)"
            @error="error = $event"
            :showSelect="false"
          ></pbas-data-table>
        </material-card>

        <material-card
          :title="$t('views.plangeneralparameters.plan_rules')"
          :color="$vuetify.theme.themes.light.secondary"
          icon="rule_folder"
        >
          <v-card-text v-if="editedItem.HasEligibilityBank">
            <eligibility-bank-rules :planIdentity="editedItem" />
          </v-card-text>
          <v-card-text v-else>
            {{ $t("views.plangeneralparameters.plan_entry") }}
            <plan-member-eligibility-rules :planIdentity="editedItem" />
            {{ $t("views.plangeneralparameters.coverage_commencement") }}
            <pbas-data-table
              :exporters="$exporters"
              ref="overageCommencementRulesTable"
              :dataProvider="coverageCommencementRulesProvider"
              fileNamePrefix="coverage-commencement-rules"
              identifier="coverageCommencementRulesTable"
              @error="error = $event"
              :showSelect="false"
            ></pbas-data-table>
            {{ $t("views.plangeneralparameters.ongoing_coverage_eligibility") }}
            <pbas-data-table
              :exporters="$exporters"
              ref="rulesTable"
              :dataProvider="ongoingCoverageEligibilityRulesProvider"
              fileNamePrefix="ongoing-coverage-eligibility-rules"
              identifier="ongoingCoverageEligibilityRulesTable"
              @error="error = $event"
              :showSelect="false"
            ></pbas-data-table>
          </v-card-text>
        </material-card>
      </template>

      <!-- </v-container> -->
    </div>
    <error-handler :error="error"></error-handler>
  </div>
  <div v-else>
    <v-row justify="center">
      <v-progress-circular
        :size="70"
        :width="7"
        color="accent"
        indeterminate
      ></v-progress-circular>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FlagForm from "../components/FlagForm";
import Contacts from "@/components/Contacts.vue";
import ErrorHandler from "../components/ErrorHandler";
import { InMemoryProvider } from "pbas-vue-components";
import FlagsHeaders from "../headers/Flags";
import { locker } from "../mixins/locker";
import { planSetter } from "../mixins/planSetter";
import { ODataProvider } from "pbas-vue-components";
import PensionRegulatorsHeaders from "../headers/PensionRegulators";
import PensionRegulatorsExpansionHeaders from "../headers/PensionRegulatorsExpansion";
import PlanIdentity from "@/components/PlanGeneralParameters/PlanIdentity.vue";
import PensionsPlanIdentity from "@/components/PlanGeneralParameters/PensionsPlanIdentity.vue";
import PlanMemberEligibilityRules from "@/components/PlanGeneralParameters/PlanMemberEligibilityRules.vue";
import OngoingEligibilityHeaders from "../headers/OngoingEligibility";
import CoverageCommencementEligibilityHeaders from "../headers/CoverageCommencementEligibility";
import EligibilityBankRules from "@/components/PlanGeneralParameters/EligibilityBankRules.vue";

export default {
  name: "plans-parameters-info",
  components: {
    ErrorHandler,
    FlagForm,
    Contacts,
    PlanIdentity,
    PensionsPlanIdentity,
    PlanMemberEligibilityRules,
    EligibilityBankRules,
  },
  mixins: [locker, planSetter],
  data() {
    return {
      flagValid: false,
      error: null,
      flagsProvider: null,
      rowActions: [
        {
          name: "editFlag",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => this.editFlag(item),
          visible: () => !this.selectedPlan.Current.IsTesting,
        },
        {
          name: "removeFlag",
          icon: "delete",
          title: "globals.remove",
          handler: async (item) => {
            try {
              this.flag = (
                await this.lock(
                  this.getFlag,
                  {
                    id: item.Id,
                    planId: this.selectedPlan.Id,
                    dialogs: this.dialogs,
                    dialog: "delete",
                  },
                  () => {
                    this.dialogs.delete = false;
                  }
                )
              )[0];

              this.dialogs.delete = true;
            } catch (error) {
              this.error = error;
            }
          },
          visible: () => !this.selectedPlan.Current.IsTesting,
        },
      ],
      headers: FlagsHeaders,
      saving: false,
      isNew: false,
      dialogs: {
        flag: false,
        delete: false,
      },
      flag: null,
      editedItem: null,
      originalItem: null,
      edit: false,
      regulatorsProvider: undefined,
      coverageCommencementRulesProvider: undefined,
      ongoingCoverageEligibilityRulesProvider: undefined,
    };
  },
  computed: {
    ...mapGetters("common", ["language", "winZones"]),
    ...mapGetters("auth", ["isLoggedIn", "user"]),
    ...mapGetters("common", { selectedPlan: "plan" }),
    ...mapGetters("plansMembers", { columnsDropDownOptions: "options" }),
    floatActions() {
      if (this.selectedPlan.Current.IsTesting) {
        return [];
      }
      return [
        {
          name: "addFlag",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => this.editFlag({ PlanIdentityId: this.editedItem.Id }),
        },
      ];
    },
    flagFormTitle() {
      return this.isNew
        ? "views.plangeneralparameters.create_new_flag"
        : "views.plangeneralparameters.edit_flag";
    },
    flagFormIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew
        ? this.$vuetify.theme.themes.light.success
        : this.$vuetify.theme.themes.light.primary;
    },
  },
  watch: {
    edit: {
      async handler(v) {
        if (v) {
          try {
            this.saving = true;
            this.editedItem = (await this.getPlan(this.selectedPlan.Id)).data;
          } catch (error) {
            this.error = error;
          } finally {
            this.saving = false;
          }
        } else {
          this.unlock();
          this.initialize();
        }
      },
    },
    selectedPlan: {
      handler(v) {
        if (v !== null && v !== undefined) {
          this.initialize();
        }
      },
    },
  },
  async mounted() {
    if (this.selectedPlan !== null && this.selectedPlan !== undefined) {
      this.initialize();
    }
  },
  methods: {
    ...mapActions("plansMembers", [
      "getPlan",
      "updateFlag",
      "newFlag",
      "deleteFlag",
      "getColumnsOptions",
      "getFlag"
    ]),
    async editFlag(item) {
      this.isNew = item.Id === null || item.Id === undefined;
      try {
        if (this.isNew) {
          this.flag = JSON.parse(JSON.stringify(item));
        } else {
          this.flag = (
            await this.lock(
              this.getFlag,
              {
                id: item.Id,
                planId: this.selectedPlan.Id,
                dialogs: this.dialogs,
                dialog: "flag",
              },
              () => {
                this.dialogs.flag = false;
              }
            )
          )[0];
        }
      } catch (error) {
        this.error = error;
      }
      if (this.flag.Color === null) this.flag.Color = "#FF0000";
      // else if (this.flag.Color.) this.flag.Color ===
      this.dialogs.flag = true;
    },
    async saveFlag() {
      //console.log("1: ", this.flag, this.flag.Color, this.flag.Color.hex);
      let flag = JSON.parse(JSON.stringify(this.flag));
      if (flag.Color.hex) flag.Color = flag.Color.hex;
      flag.Name = flag.Translations.filter(
        (o) => o.LanguageCode === this.language.language
      )[0].Name;
      if (this.isNew) {
        this.addFlag(flag);
      } else {
        this.tryUpdateFlag(flag);
      }
    },
    async tryUpdateFlag(flag) {
      this.saving = true;
      try {
        await this.updateFlag({ plan: this.selectedPlan.Id, flag: flag });
      } catch (error) {
        this.error = error;
      }
      this.refreshFlags();
      this.closeFlag();
      this.saving = false;
    },
    async addFlag(flag) {
      this.saving = true;
      try {
        await this.newFlag({ plan: this.selectedPlan.Id, flag: flag });
      } catch (error) {
        this.error = error;
      }
      this.refreshFlags();
      this.closeFlag();
      this.saving = false;
    },
    async removeFlag() {
      this.saving = true;
      try {
        await this.deleteFlag({
          plan: this.selectedPlan.Id,
          flagId: this.flag.Id,
        });
      } catch (error) {
        this.error = error;
      }
      this.refreshFlags();
      this.dialogs.delete = false;
      this.saving = false;
      this.flag = null;
    },
    async refreshFlags() {
      try {
        let flags = (await this.getPlan(this.selectedPlan.Id)).data.Flags;
        this.flagsProvider.items = flags;
        //console.log("refresh: ", flags, this.flagsProvider.items);
        this.$refs.table.getDataAsync();
      } catch (error) {
        this.error = error;
      }
    },
    closeFlag() {
      this.flag = null;
      // this.$refs.flagForm.resetValidation();
      this.dialogs.flag = false;
    },
    async initialize() {
      this.saving = true;
      try {
        this.editedItem = (await this.getPlan(this.selectedPlan.Id)).data;
        //console.log(this.editedItem);
        this.saving = false;
        this.flagsProvider = new InMemoryProvider(
          this.editedItem.Flags,
          this.headers
        );

        this.regulatorsProvider = new ODataProvider(
          this.$axiosHttp,
          "pension-regulators",
          PensionRegulatorsHeaders
        );

        this.coverageCommencementRulesProvider = new ODataProvider(
          this.$axiosHttp,
          `plans/${this.selectedPlan.Id}/eligibility-rules`,
          CoverageCommencementEligibilityHeaders
        );
        this.coverageCommencementRulesProvider.filter =
          "RuleType eq 'CoverageCommencement'";
        this.ongoingCoverageEligibilityRulesProvider = new ODataProvider(
          this.$axiosHttp,
          `plans/${this.selectedPlan.Id}/eligibility-rules`,
          OngoingEligibilityHeaders
        );
        this.ongoingCoverageEligibilityRulesProvider.filter =
          "RuleType eq 'CoverageEligibility'";
      } catch (error) {
        this.error = error;
      }
    },
    async saved(v) {
      this.editedItem = JSON.parse(JSON.stringify(v));
      this.edit = false;
    },
    generateProvider(item) {
      let url = "pension-regulators/" + item.Id + "/history";
      return new ODataProvider(
        this.$axiosHttp,
        url,
        PensionRegulatorsExpansionHeaders
      );
    },
  },
};
</script>

<style>
.full-width {
  width: 100%;
}
.padding-top-14 {
  padding-top: 14px !important;
}
.padding-top-17 {
  padding-top: 17px !important;
}
</style>
<style scoped>
.minfo {
  font-weight: bold;
  color: #222;
}
</style>
